import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import FilterBar from "../../../../components/FilterNSearchBar/FilterNSearchBar";
import {
  CARD_HEADER_SX,
  FILTER_BAR_WRAP_SX,
  countLabel,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import RefreshButton from "../../components/RefreshButton";

// Card + header + search + table, same shape as the Code Security list.
const ListCard = ({
  title,
  subtitle,
  titleExtra,
  refreshQueries,
  addLabel,
  onAdd,
  searchQuery,
  onSearchChange,
  filters = [],
  selectedFilter = null,
  onFilterChange = () => {},
  onClearFilters,
  countNoun,
  columns,
  rows,
  renderCell,
  renderRowFooter,
  getRowProps,
  getRowSx,
  loading = false,
  toolbar,
  emptyText,
  sortBy,
  onSortChange,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <CardHeader
        sx={CARD_HEADER_SX}
        title={
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              {title}
            </Typography>
            {titleExtra}
          </Stack>
        }
        subheader={
          subtitle && (
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, mt: 0.25 }}
            >
              {subtitle}
            </Typography>
          )
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <RefreshButton label={title} queries={refreshQueries} />
            {onAdd && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAdd}
                sx={primaryButtonSx(theme)}
              >
                {addLabel}
              </Button>
            )}
          </Stack>
        }
      />
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {(toolbar || onSearchChange) && (
          <Box sx={FILTER_BAR_WRAP_SX}>
            {toolbar && <Box sx={{ mb: onSearchChange ? 1.5 : 0 }}>{toolbar}</Box>}
            {onSearchChange && (
              <FilterBar
                searchQuery={searchQuery}
                onSearchChange={(event) => onSearchChange(event.target.value)}
                filters={filters}
                selectedFilter={selectedFilter}
                onFilterChange={onFilterChange}
                onClearFilters={onClearFilters}
                countLabel={countLabel(rows.length, countNoun)}
              />
            )}
          </Box>
        )}
        <Divider sx={{ borderColor: theme.customColors.border.main }} />
        <Box
          aria-busy={loading || undefined}
          {...(loading
            ? { role: "status", "aria-label": `Loading ${title}` }
            : {})}
        >
          <ReusableTable
            columns={columns}
            data={rows}
            renderCell={renderCell}
            renderRowFooter={renderRowFooter}
            getRowProps={getRowProps}
            getRowSx={getRowSx}
            loading={loading}
            sortBy={sortBy}
            onSortChange={onSortChange}
            showPagination={rows.length > 10}
            preservePage
            emptyState={
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  p: 3,
                  textAlign: "center",
                }}
              >
                {emptyText}
              </Typography>
            }
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ListCard;
